
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { useIsMobile } from "@/hooks/use-mobile";
import StorageUsage from "@/components/StorageUsage";
import {
  MessageSquarePlus,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  LogOut,
  Settings as SettingsIcon,
  Edit,
  Trash2,
  User,
  Search,
  FileText,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Conversation } from "@/types";

interface ConversationItemProps {
  conversation: Conversation;
  selected: boolean;
  onRename: (id: string, newTitle: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [conversationToRename, setConversationToRename] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const { user, logout } = useAuth();
  const { conversations, createNewConversation, currentConversation, deleteConversation, renameConversation } = useChat();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const isMobileDevice = useIsMobile();
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    setIsMobile(isMobileDevice);
    
    // Auto-collapse on mobile
    if (isMobileDevice && !collapsed) {
      setCollapsed(true);
    }
  }, [isMobileDevice]);
  
  const handleCreateNewConversation = async () => {
    await createNewConversation();
  };
  
  const handleDeleteConversation = async (id: string) => {
    setConversationToDelete(id);
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    if (conversationToDelete) {
      await deleteConversation(conversationToDelete);
      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    }
  };
  
  const handleRenameConversation = async (id: string, title: string) => {
    setConversationToRename(id);
    setNewTitle(title);
    setRenameDialogOpen(true);
  };
  
  const confirmRename = async () => {
    if (conversationToRename && newTitle.trim()) {
      await renameConversation(conversationToRename, newTitle);
      setRenameDialogOpen(false);
      setConversationToRename(null);
      setNewTitle("");
    }
  };
  
  const handleSignOut = async () => {
    await logout();
    navigate("/");
  };
  
  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };
  
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };
  
  const filteredConversations = conversations.filter((conversation) => 
    conversation.title.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const renderConversationItem = ({ conversation, selected, onRename, onDelete }: ConversationItemProps) => {
    const formattedDate = new Date(conversation.created_at).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
    
    // Truncate title for sidebar - reduced maximum length to account for action buttons
    const displayTitle = conversation.title.length > 22
      ? conversation.title.substring(0, 22) + "..."
      : conversation.title;
      
    // Truncate last message for sidebar
    const displayLastMessage = conversation.last_message?.length > 40
      ? conversation.last_message.substring(0, 40) + "..."
      : conversation.last_message;
    
    return (
      <div 
        key={conversation.id}
        className={`
          relative px-2 py-1.5 mb-1 rounded-md cursor-pointer
          transition-colors group flex items-start
          ${selected ? "bg-secondary" : "hover:bg-secondary/50"}
        `}
        onClick={() => navigate(`/chat/${conversation.id}`)}
      >
        <div className="flex-1 min-w-0 overflow-hidden pr-8">
          <div className="flex justify-between items-start">
            <span 
              className={`font-medium truncate text-sm ${selected ? "" : "text-foreground/90"}`}
              title={conversation.title}
            >
              {displayTitle}
            </span>
            <span className="text-xs text-muted-foreground ml-1 flex-shrink-0">
              {formattedDate}
            </span>
          </div>
          
          {!collapsed && displayLastMessage && (
            <p 
              className="text-xs text-muted-foreground truncate mt-0.5 line-clamp-1" 
              title={conversation.last_message}
            >
              {displayLastMessage}
            </p>
          )}
        </div>
        
        {!collapsed && (
          <div className={`
            absolute right-2 top-1.5 flex gap-0.5 z-10 opacity-100
            ${selected ? "" : "group-hover:opacity-100"}
          `}>
            <Button 
              onClick={(e) => {
                e.stopPropagation();
                onRename(conversation.id, conversation.title);
              }}
              variant="ghost" 
              size="icon"
              className="h-5 w-5"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button 
              onClick={(e) => {
                e.stopPropagation();
                onDelete(conversation.id);
              }}
              variant="ghost" 
              size="icon"
              className="h-5 w-5"
            >
              <Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
        )}
      </div>
    );
  };
  
  if (!user) {
    return null;
  }

  return (
    <>
      <div 
        ref={sidebarRef}
        data-tour="conversation-sidebar"
        className={`
          border-r bg-card flex flex-col transition-all duration-300 ease-in-out
          ${collapsed ? 'w-[60px]' : 'w-[360px]'}
          ${isMobile && !collapsed ? 'fixed inset-y-0 left-0 z-50 shadow-xl' : ''}
        `}
      >
        
        <div className="p-2">
          {collapsed ? (
            <div className="flex flex-col gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-tour="new-conversation-btn"
                      variant="default"
                      size="icon"
                      className="w-full h-12 bg-primary hover:bg-primary/90 shadow-lg ring-2 ring-primary/20 hover:ring-primary/40 transition-all duration-200"
                      onClick={handleCreateNewConversation}
                    >
                      <MessageSquarePlus className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    Nova Conversa
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
            </div>
          ) : (
            <div className="flex justify-center">
              <Button
                data-tour="new-conversation-btn"
                variant="default"
                className="h-12 flex flex-col gap-1 text-xs bg-primary hover:bg-primary/90 shadow-lg ring-2 ring-primary/20 hover:ring-primary/40 transition-all duration-200"
                onClick={handleCreateNewConversation}
              >
                <MessageSquarePlus className="h-5 w-5" />
                <span className="font-medium">Nova Conversa</span>
              </Button>
            </div>
          )}
        </div>
        
        {!collapsed && (
          <div className="px-2 mb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                data-tour="search-conversations"
                type="text"
                placeholder="Buscar conversas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
              {searchTerm && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="absolute right-1 top-1 h-7 w-7"
                  onClick={() => setSearchTerm("")}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}
        
        <ScrollArea data-tour="conversations-list" className="flex-1 px-1">
          <div className="space-y-1">
            {!collapsed && filteredConversations.length > 0 && (
              <h3 className="text-xs font-medium text-muted-foreground mb-2 ml-2">
                {searchTerm ? "Resultados da busca" : "Conversas recentes"}
              </h3>
            )}
            
            {filteredConversations.map((conversation) => renderConversationItem({
              conversation,
              selected: conversation.id === conversationId,
              onRename: handleRenameConversation,
              onDelete: handleDeleteConversation
            }))}
            
            {searchTerm && filteredConversations.length === 0 && (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Nenhuma conversa encontrada para "{searchTerm}"
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
        
      </div>
      
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conversa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renomear conversa</DialogTitle>
          </DialogHeader>
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Novo título"
            className="mt-2"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmRename}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Sidebar;
